import { mdiPencil } from "@mdi/js";
import { Button, Space } from "antd";
import confirm from "antd/lib/modal/confirm";
import { SimpleFailureStatusVm } from "api/generated/lumen";
import { useDispatch, useSelector } from "app/store";
import { MaterialIcon } from "components/MaterialIcon";
import CheckboxDisplay from "components/ui/CheckboxDisplay";
import useTable from "hooks/useTable";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "widgets/table/CustomTable";
import { ColumnStorageName } from "widgets/table/table-settings/ColumnStorageName";
import { ColumnConfigParams } from "widgets/table/useTableUtils";
import styles from "./StatusChaneRules.module.scss";
import Claims from "features/auth/claims";
import { Permission } from "components/Permission";
import { useBlocker } from "react-router-dom";
import useNavigationBlocker from "widgets/form/hooks/useNavigationBlocker";

type TransformedRelationType = Record<
  string,
  Record<string, boolean | undefined>
>;

const StatusChangeRules = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isEditing, setEditing] = useState<boolean>(false);
  const changedValues = useRef<TransformedRelationType>({});
  const { statusRules, isSaveDisabled } = useSelector(
    (state) => state.statuses
  );
  const { fetchStatusRules, updateStatusRules } = useSelector(
    (state) => state.loading.effects.statuses
  );

  const statuses = useMemo(() => {
    return [...(statusRules?.statuses ?? [])].sort((a, b) =>
      a.name && b.name ? a.name.localeCompare(b.name) : 0
    );
  }, [statusRules?.statuses]);

  const rules = useMemo(() => {
    return statusRules?.rules;
  }, [statusRules?.rules]);

  const transformedRelations = useMemo(
    () =>
      rules?.reduce<TransformedRelationType>((prev, { fromId, toId }) => {
        if (!fromId || !toId) {
          return prev;
        }

        if (!prev[fromId]) {
          prev[fromId] = { [toId]: true };
        } else {
          prev[fromId][toId] = true;
        }

        return prev;
      }, {}),
    [rules]
  );

  const addOrRemoveRelation = useCallback(
    ({
      statusFromId,
      statusToId,
      checked,
    }: {
      statusFromId: string;
      statusToId: string;
      checked: boolean;
    }) => {
      if (checked === !!transformedRelations?.[statusFromId]?.[statusToId]) {
        delete changedValues.current[statusFromId]?.[statusToId];

        if (Object.keys(changedValues.current[statusFromId]).length === 0) {
          delete changedValues.current[statusFromId];
        }

        if (Object.keys(changedValues.current).length === 0) {
          dispatch.statuses.setIsSaveDisabled(true);
        } else {
          dispatch.statuses.setIsSaveDisabled(false);
        }
        return;
      }

      changedValues.current = {
        ...changedValues.current,
        [statusFromId]: {
          ...(changedValues.current[statusFromId] ?? {}),
          [statusToId]: checked,
        },
      };

      if (Object.keys(changedValues.current).length !== 0) {
        dispatch.statuses.setIsSaveDisabled(false);
      }
    },
    [transformedRelations, dispatch.statuses]
  );

  const fetchTableData = useCallback(() => {
    return dispatch.statuses.fetchStatusRules();
  }, [dispatch.statuses]);

  const resetChangedValues = useCallback(() => {
    changedValues.current = {};
    fetchTableData();
    dispatch.statuses.resetStatusRules();
    dispatch.statuses.setIsSaveDisabled(true);
  }, [fetchTableData, dispatch.statuses]);

  const handleSaveRelations = useCallback(async () => {
    const relations = Object.entries(changedValues.current).flatMap(
      ([statusFromId, statusToIds]) =>
        Object.entries(statusToIds).map(([statusToId, relation]) => ({
          fromId: statusFromId,
          toId: statusToId,
          add: relation,
        }))
    );

    if (relations) {
      confirm({
        okText: t("common.confirmDeleteModal.yes"),
        okType: "danger",
        cancelText: t("common.confirmDeleteModal.no"),
        title: t("settings.statuses.statusChangeRoles.confirmSaveModal.title"),
        onOk: async () => {
          await dispatch.statuses.updateStatusRules(relations);

          setEditing(false);
          resetChangedValues();
        },
        onCancel: () => {
          resetChangedValues();
          setEditing(false);
        },
      });
    }
  }, [resetChangedValues, t, dispatch.statuses]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return (
      currentLocation.pathname !== nextLocation.pathname && !isSaveDisabled
    );
  });

  useNavigationBlocker({ blocker });

  const columnParams = useMemo<
    ColumnConfigParams<SimpleFailureStatusVm>[]
  >(() => {
    return [
      {
        key: "statusIds",
        dataIndex: "name",
        title: <>{t("settings.statuses.name")}</>,
        width: "8rem",
        fixed: "left",
      },
      ...(statuses ?? []).map((statusTo) => {
        return {
          key: `status-${statusTo.id}`,
          dataIndex: "id",
          title: <div className={styles.cell}>{statusTo.name}</div>,
          width: "11rem",
          render: (statusFromId: string) => (
            <div className={styles.cell}>
              <CheckboxDisplay
                className={styles.cell}
                disabled={
                  !isEditing ||
                  statuses.find((status) => status.id === statusFromId)
                    ?.isSealed ||
                  statusFromId === statusTo.id
                }
                isEditing={isEditing}
                value={
                  statusTo.id
                    ? {
                        ...transformedRelations,
                        ...changedValues.current,
                      }[statusFromId]?.[statusTo.id] ?? false
                    : false
                }
                onChange={(checked) => {
                  if (!statusTo.id) {
                    return;
                  }
                  addOrRemoveRelation({
                    statusFromId,
                    statusToId: statusTo.id,
                    checked,
                  });
                }}
              />
            </div>
          ),
        };
      }),
      {
        key: "emptyCol",
        fixed: "right",
        render: () => <></>,
      },
    ];
  }, [t, isEditing, statuses, transformedRelations, addOrRemoveRelation]);

  const customTableProps = useTable({
    fetchTableData,
    columnParams,
    list: statuses,
    columnStorageName: ColumnStorageName.STATUSES_RULES,
    selectMode: "multiple",
    isFilterOption: false,
    disableTableSettings: true,
    headerOptions: (
      <Space>
        {isEditing ? (
          <Space>
            <Button
              type="text"
              onClick={() => {
                if (!isSaveDisabled) {
                  confirm({
                    okText: t("common.confirmDeleteModal.yes"),
                    okType: "danger",
                    cancelText: t("common.confirmDeleteModal.no"),
                    title: t(
                      "settings.statusChangeRules.confirmCancelModal.title"
                    ),
                    onOk: async () => {
                      setEditing(false);
                      resetChangedValues();
                    },
                  });
                } else {
                  setEditing(false);
                }
              }}
            >
              {t("common.cancel")}
            </Button>

            <Button
              type="primary"
              loading={updateStatusRules.loading}
              onClick={handleSaveRelations}
              disabled={isSaveDisabled}
            >
              {t("common.save")}
            </Button>
          </Space>
        ) : (
          <Permission acceptableClaims={[Claims.FailureStatus.Write]}>
            <Button
              icon={<MaterialIcon path={mdiPencil} />}
              loading={updateStatusRules.loading}
              onClick={() => setEditing(true)}
            >
              {t("common.edit")}
            </Button>
          </Permission>
        )}
      </Space>
    ),
    resetStore: dispatch.statuses.resetStatusRules,
    loading: fetchStatusRules.loading,
  });

  return (
    <CustomTable<SimpleFailureStatusVm>
      {...customTableProps}
      className={styles.table}
      scrollX={"max-content"}
      selectable={false}
    />
  );
};

export default StatusChangeRules;
